import { useEffect, useState, useRef } from 'react';
import { useMonitorRunStream } from "@/hooks/useMonitorRunStream";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { 
    Loader2, Activity, CheckCircle2, 
    XCircle, Image as ImageIcon, Maximize2, X, Code2,
    Globe, Shield, FileJson, ChevronDown, Copy, Check, Terminal, ExternalLink, Link, Monitor
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { TruncatedUrl } from "@/components/ui/truncated-url";
import { useToast } from "@/components/ui/use-toast";
import StatusBotIcon from "@/components/StatusBotIcon";

// Format duration in a human-readable way
const formatDuration = (ms) => {
    if (!ms && ms !== 0) return 'N/A';

    if (ms < 1000) {
        return `${Math.round(ms)}ms`;
    } else if (ms < 60000) {
        const seconds = (ms / 1000).toFixed(1);
        return `${seconds}s`;
    } else {
        const minutes = Math.floor(ms / 60000);
        const seconds = ((ms % 60000) / 1000).toFixed(1);
        return `${minutes}m ${seconds}s`;
    }
};

function DataSection({ title, children, icon: Icon, iconClassName, defaultExpanded = true, disabled = false, alwaysExpanded = false }) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    
    // Update expanded state when defaultExpanded changes (e.g., when run completes)
    useEffect(() => {
        if (defaultExpanded && !disabled) {
            setExpanded(true);
        }
    }, [defaultExpanded, disabled]);
    
    // If alwaysExpanded is true, always show as expanded
    const isExpanded = alwaysExpanded || expanded;

    return (
        <div className={cn(
            "animate-in slide-in-from-bottom-2 fade-in duration-300 transition-opacity duration-300",
            disabled && "opacity-50"
        )}>
            <div className="relative bg-black/20 backdrop-blur-sm border border-cyan-500/30 rounded-lg overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-cyan-500/5 blur-2xl" />
                
                <button
                    onClick={() => !disabled && !alwaysExpanded && setExpanded(!expanded)}
                    disabled={disabled}
                    className={cn(
                        "relative w-full flex items-center justify-between p-3 transition-colors",
                        disabled ? "cursor-not-allowed" : alwaysExpanded ? "cursor-default" : "hover:bg-cyan-500/5 cursor-pointer"
                    )}
                >
                    <div className="flex items-center gap-2">
                        {Icon && <Icon className={iconClassName || "h-4 w-4 text-cyan-400"} />}
                        <span className="text-sm font-medium text-cyan-400">{title}</span>
                    </div>
                    {!alwaysExpanded && (
                        <ChevronDown className={cn(
                            "h-4 w-4 text-cyan-400 transition-transform",
                            isExpanded && !disabled && "rotate-180"
                        )} />
                    )}
                </button>

                {isExpanded && !disabled && (
                    <div className="relative p-3 pt-0">
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
}

function CopyButton({ text }) {
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast({
                title: "Copied to clipboard",
                description: "Content has been copied.",
            });
        } catch (err) {
            toast({
                title: "Failed to copy",
                description: "Please try copying manually.",
                variant: "destructive",
            });
        }
    };

    return (
        <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            onClick={handleCopy}
        >
            {copied ? (
                <Check className="h-3 w-3" />
            ) : (
                <Copy className="h-3 w-3" />
            )}
        </Button>
    );
}

function BrowserScreenshot({ screenshot, stepName, onExpand }) {
    const [loaded, setLoaded] = useState(false);
    
    const imageSrc = screenshot?.startsWith('data:') 
        ? screenshot 
        : `data:image/png;base64,${screenshot}`;

    return (
        <div className="relative group animate-in fade-in zoom-in-95 duration-300">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-cyan-600 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-200" />
            <div className="relative bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-2 overflow-hidden">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <ImageIcon className="h-3 w-3 text-cyan-400" />
                        <span className="text-xs font-mono text-cyan-400">
                            {stepName ? `${stepName}_CAPTURE` : 'SCREENSHOT_CAPTURE'}
                        </span>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10"
                        onClick={() => onExpand(imageSrc)}
                    >
                        <Maximize2 className="h-3 w-3" />
                    </Button>
                </div>
                
                {!loaded && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
                    </div>
                )}
                
                <img 
                    src={imageSrc}
                    alt={stepName ? `${stepName} screenshot` : 'Screenshot'}
                    className={cn(
                        "w-full rounded border border-cyan-500/20 transition-opacity duration-300",
                        loaded ? "opacity-100" : "opacity-0"
                    )}
                    onLoad={() => setLoaded(true)}
                />
            </div>
        </div>
    );
}

function LiveStep({ step, index, isComplete }) {
    if (!step) return null;

    const hasData = step.data && Object.keys(step.data).length > 0;
    const isSystemEvent = !step.name || step.name.includes('connecting') || step.name.includes('watching');

    return (
        <div className="animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className={cn(
                "relative backdrop-blur-sm rounded-lg p-3 transition-colors",
                isSystemEvent 
                    ? "bg-black/20 border border-green-500/30 hover:border-green-500/50" 
                    : "bg-black/20 border border-purple-500/30 hover:border-purple-500/50"
            )}>
                {/* Glowing corner accent */}
                <div className={cn(
                    "absolute top-0 right-0 w-20 h-20 blur-2xl",
                    isSystemEvent ? "bg-green-500/10" : "bg-purple-500/10"
                )} />
                
                <div className="relative">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                            <div className={cn(
                                "h-2 w-2 rounded-full mt-1.5 shrink-0",
                                isComplete ? "bg-purple-500/40" : "bg-purple-500 animate-pulse"
                            )} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={cn(
                                        "text-sm font-mono break-words",
                                        isSystemEvent ? "text-green-400" : "text-purple-400"
                                    )}>
                                        {step.name || (isSystemEvent ? 'SYSTEM_EVENT' : 'EVENT')}
                                    </span>
                                    {hasData && step.data.success !== undefined && (
                                        <Badge 
                                            variant="outline" 
                                            className={cn(
                                                "text-xs font-mono",
                                                step.data.success 
                                                    ? "border-green-500/50 text-green-400" 
                                                    : "border-red-500/50 text-red-400"
                                            )}
                                        >
                                            {step.data.success ? 'SUCCESS' : 'FAILED'}
                                        </Badge>
                                    )}
                                    {hasData && step.data.duration && (
                                        <Badge variant="outline" className="border-cyan-500/50 text-cyan-400 text-xs font-mono">
                                            {step.data.duration}ms
                                        </Badge>
                                    )}
                                </div>
                                
                                {/* Show key data points inline */}
                                {hasData && (
                                    <div className="mt-2 space-y-1">
                                        {step.data.error && (
                                            <div className="text-xs font-medium text-red-400 bg-red-500/10 rounded px-2 py-1 border border-red-500/30">
                                                Error: {step.data.error}
                                            </div>
                                        )}
                                        {step.data.finalResult && (
                                            <div className="text-xs font-mono text-cyan-400">
                                                {step.data.finalResult}
                                            </div>
                                        )}
                                        {/* Show other data in compact format */}
                                        {Object.entries(step.data)
                                            .filter(([key]) => !['success', 'duration', 'error', 'finalResult', 'totalSteps'].includes(key))
                                            .map(([key, value]) => (
                                                <div key={key} className="text-xs text-purple-300/70">
                                                    <span className="font-medium">{key}:</span> <span className="font-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                                </div>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                        {step.timestamp && (
                            <span className="text-xs font-mono text-purple-400/50 shrink-0 mt-0.5">
                                {new Date(step.timestamp).toLocaleTimeString('en-US', { 
                                    hour12: false,
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit'
                                })}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function BrowserAgentStep({ step, stepName, index, onExpandScreenshot }) {
    if (!step) return null;

    return (
        <div className="animate-in slide-in-from-bottom-2 fade-in duration-300">
            <div className="relative bg-black/20 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-4">
                {/* Glowing accent */}
                <div className="absolute top-0 left-0 w-20 h-20 bg-cyan-500/10 blur-2xl" />
                
                <div className="relative space-y-3">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-cyan-500 animate-pulse" />
                            <span className="text-sm font-mono text-cyan-400">
                                {stepName || step.action || 'BROWSER_EVENT'}
                            </span>
                            {step.action && (
                                <Badge variant="outline" className="border-cyan-500/50 text-cyan-400 text-xs">
                                    {step.action}
                                </Badge>
                            )}
                        </div>
                        {step.error ? (
                            <XCircle className="h-4 w-4 text-red-500" />
                        ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                        )}
                    </div>

                    {step.url && (
                        <div className="flex items-center gap-2">
                            <Code2 className="h-3 w-3 text-purple-300/70" />
                            <span className="text-xs font-mono text-purple-300/70 truncate">
                                {step.url}
                            </span>
                        </div>
                    )}

                    {step.extractedContent && (
                        <div className="bg-black/40 rounded border border-green-500/30 p-2">
                            <div className="text-xs font-medium text-green-400 mb-1">Extracted Data:</div>
                            <div className="text-xs font-mono text-purple-200">
                                {step.extractedContent}
                            </div>
                        </div>
                    )}

                    {step.error && (
                        <div className="bg-red-500/10 rounded border border-red-500/30 p-2">
                            <div className="text-xs font-medium text-red-400 mb-1">Error:</div>
                            <div className="text-xs font-mono text-red-300">
                                {step.error}
                            </div>
                        </div>
                    )}

                    {step.screenshot && (
                        <BrowserScreenshot 
                            screenshot={step.screenshot} 
                            stepName={stepName}
                            onExpand={onExpandScreenshot}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

export function LiveMonitorRunStream({ runId, open, onOpenChange, monitorName }) {
    const [selectedStepIndex, setSelectedStepIndex] = useState(null); // Track which step's screenshot to show
    const [lightboxScreenshot, setLightboxScreenshot] = useState(null); // For fullscreen view
    const [liveDuration, setLiveDuration] = useState(0); // Live duration counter while running
    const [dedupeSteps, setDedupeSteps] = useState(true); // Toggle for deduplication
    const timelineEndRef = useRef(null); // Ref to scroll to the bottom of timeline
    const hasUserSelectedStepRef = useRef(false); // Track manual selection vs auto-follow
    
    const { 
        runData, 
        steps: liveSteps, 
        isComplete, 
        completionData,
        isConnected,
        isInitialized 
    } = useMonitorRunStream(runId, open);

    // Live duration counter - updates every 100ms while run is in progress
    useEffect(() => {
        if (!runData?.startTime || isComplete) {
            return;
        }

        const startTime = new Date(runData.startTime).getTime();
        
        const updateDuration = () => {
            const now = Date.now();
            setLiveDuration(now - startTime);
        };

        // Update immediately
        updateDuration();

        // Then update every 100ms for smooth counter
        const interval = setInterval(updateDuration, 100);

        return () => clearInterval(interval);
    }, [runData?.startTime, isComplete]);

    // Get the screenshot to display - either selected or latest
    const displayedScreenshot = (() => {
        if (!liveSteps || liveSteps.length === 0) return null;
        
        // If user selected a specific step, find the last screenshot at or before that index
        if (selectedStepIndex !== null) {
            // Look backwards from selected index to find a screenshot
            for (let i = selectedStepIndex; i >= 0; i--) {
                if (liveSteps[i]?.data?.screenshot) {
                    return {
                        src: liveSteps[i].data.screenshot,
                        stepName: liveSteps[i].name,
                        stepIndex: i,
                        url: liveSteps[i].data?.url,
                        selectedIndex: selectedStepIndex // Track which step is actually selected
                    };
                }
            }
            // If no screenshot found before selected step, return null
            return null;
        }
        
        // Otherwise, find the most recent step with a screenshot
        for (let i = liveSteps.length - 1; i >= 0; i--) {
            if (liveSteps[i]?.data?.screenshot) {
                return {
                    src: liveSteps[i].data.screenshot,
                    stepName: liveSteps[i].name,
                    stepIndex: i,
                    url: liveSteps[i].data?.url,
                    selectedIndex: null
                };
            }
        }
        return null;
    })();

    const handleStepClick = (index) => {
        // Allow clicking any step - we'll find the screenshot to display in displayedScreenshot
        hasUserSelectedStepRef.current = true;
        setSelectedStepIndex(index);
    };

    // Default-select the latest step (and keep following) unless user manually selects a step
    useEffect(() => {
        if (!liveSteps || liveSteps.length === 0) return;
        if (hasUserSelectedStepRef.current) return;

        setSelectedStepIndex(liveSteps.length - 1);
        if (timelineEndRef.current) {
            timelineEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [liveSteps]);

    // Reset selection state when dialog closes so reopening re-selects latest
    useEffect(() => {
        if (open) return;
        hasUserSelectedStepRef.current = false;
        setSelectedStepIndex(null);
        setLightboxScreenshot(null);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col overflow-hidden p-0 gap-0 bg-purple-950 border-purple-500/50">
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-cyan-500/5" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.1),transparent_50%)]" />
                
                <div className="relative flex-1 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-3 h-full gap-4 px-6 pb-6 pt-4">
                        {/* Left Panel (2/3 width) - Browser View & Timeline */}
                        <div className="flex flex-col h-full min-h-[300px] lg:col-span-2 gap-4">
                            {/* Top: Browser Screenshot Display - Only show for browser_agent */}
                            {runData?.actionType === 'browser_agent' && (
                                <div className="flex flex-col flex-[2] min-h-[300px]">
                                    <div className="flex items-center gap-2 mb-3">
                                        <h3 className="text-sm font-semibold text-cyan-400">Browser View</h3>
                                        <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/50 to-transparent" />
                                    </div>
                                    
                                    <div className="relative flex-1 bg-black/60 backdrop-blur-sm border border-cyan-500/30 rounded-lg overflow-hidden group">
                                        {!displayedScreenshot ? (
                                            <div className="flex items-center justify-center h-full">
                                                    <Globe className="h-10 w-10 text-purple-700 opacity-50" />
                                            </div>
                                        ) : (
                                            <div className="h-full flex flex-col">
                                                {/* Expand button - shows on hover */}
                                                <button
                                                    onClick={() => setLightboxScreenshot(displayedScreenshot.src)}
                                                    className="absolute bottom-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 hover:bg-black/90 text-cyan-400 hover:text-cyan-300 p-2 rounded-lg border border-cyan-500/50 hover:border-cyan-500"
                                                >
                                                    <Maximize2 className="h-4 w-4" />
                                                </button>
                                                
                                                {/* Browser Chrome */}
                                                <div className="bg-purple-950/80 border-b border-cyan-500/30 px-3 py-2 flex items-center gap-2">
                                                    <div className="flex gap-1.5 shrink-0">
                                                        <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                                                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                                                        <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                                                    </div>
                                                    <div className="flex-1 ml-2 bg-black/40 rounded px-2 py-1 flex items-center gap-2 min-w-0 overflow-hidden">
                                                        <Globe className="h-3 w-3 text-purple-300/70 shrink-0" />
                                                        <span className="text-xs font-mono text-purple-300/70 truncate">
                                                            {displayedScreenshot.url || 'about:blank'}
                                                        </span>
                                                        {displayedScreenshot.url && displayedScreenshot.url !== 'about:blank' && (
                                                            <button
                                                                onClick={() => window.open(displayedScreenshot.url, '_blank', 'noopener,noreferrer')}
                                                                className="shrink-0 ml-auto text-cyan-400 hover:text-cyan-300 transition-colors"
                                                            >
                                                                <ExternalLink className="h-3 w-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Screenshot */}
                                                <div className="flex-1 overflow-auto bg-black">
                                                    <img 
                                                        src={displayedScreenshot.src?.startsWith('data:') 
                                                            ? displayedScreenshot.src 
                                                            : `data:image/png;base64,${displayedScreenshot.src}`}
                                                        alt={displayedScreenshot.stepName}
                                                        className="w-full h-auto rounded border border-cyan-500/20"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Bottom: Timeline/Log */}
                            <div className="flex flex-col flex-1 min-h-[200px]">
                                <div className="flex items-center gap-2 mb-3">
                                    <h3 className="text-sm font-semibold text-purple-400">Activity Timeline</h3>
                                    <div className="flex-1 h-px bg-gradient-to-r from-purple-500/50 to-transparent" />
                                    <div
                                        className="flex items-center"
                                        title={dedupeSteps ? "Condensed on (currently: collapse duplicates)" : "Condensed off"}
                                    >
                                        <label
                                            htmlFor="dedupe-steps-toggle"
                                            className={cn(
                                                "text-[11px] font-medium select-none",
                                                dedupeSteps ? "text-purple-300" : "text-purple-300/50"
                                            )}
                                        >
                                            Condensed
                                        </label>
                                        <Switch
                                            id="dedupe-steps-toggle"
                                            checked={dedupeSteps}
                                            onCheckedChange={setDedupeSteps}
                                            className="scale-[0.75] origin-right data-[state=checked]:bg-purple-300/70 data-[state=unchecked]:bg-purple-300/30 focus-visible:ring-purple-500/40"
                                        />
                                    </div>
                                </div>
                                
                                <ScrollArea className="flex-1 bg-black/60 backdrop-blur-sm border border-purple-500/30 rounded-lg p-3">
                                    <div className="relative">
                                        {!isInitialized ? (
                                            <div className="space-y-3 py-2">
                                                {[...Array(3)].map((_, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <div className="h-2 w-2 rounded-full bg-purple-500/20 animate-pulse" />
                                                        <div className="h-3 bg-purple-500/10 rounded animate-pulse" style={{ width: `${60 + Math.random() * 30}%` }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (!liveSteps || liveSteps.length === 0) ? (
                                            <div className="space-y-3 py-2">
                                                {[...Array(2)].map((_, i) => (
                                                    <div key={i} className="flex items-center gap-2">
                                                        <div className="h-2 w-2 rounded-full bg-purple-500/20 animate-pulse" />
                                                        <div className="h-3 bg-purple-500/10 rounded animate-pulse" style={{ width: `${50 + Math.random() * 40}%` }} />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <>
                                                {/* Timeline vertical line */}
                                                <div className="absolute left-[9px] top-2 bottom-2 w-px bg-gradient-to-b from-purple-500/30 via-purple-500/20 to-transparent" />
                                                
                                                {(() => {
                                                    // Conditionally deduplicate consecutive steps with same name
                                                    const stepsToRender = dedupeSteps ? (() => {
                                                        const dedupedSteps = [];
                                                        const indexMap = []; // Maps filtered index to first original index
                                                        const duplicateRanges = []; // Tracks all indices that map to each deduplicated step
                                                        
                                                        liveSteps.forEach((step, index) => {
                                                            if (index === 0 || step.name !== liveSteps[index - 1]?.name) {
                                                                dedupedSteps.push(step);
                                                                indexMap.push(index);
                                                                duplicateRanges.push([index]); // Start a new range
                                                            } else {
                                                                // Add this index to the last duplicate range
                                                                duplicateRanges[duplicateRanges.length - 1].push(index);
                                                            }
                                                        });
                                                        
                                                        return { steps: dedupedSteps, indexMap, duplicateRanges };
                                                    })() : {
                                                        steps: liveSteps,
                                                        indexMap: liveSteps.map((_, i) => i),
                                                        duplicateRanges: liveSteps.map((_, i) => [i])
                                                    };
                                                    
                                                    return stepsToRender.steps.map((step, filteredIndex) => {
                                                        const index = stepsToRender.indexMap[filteredIndex];
                                                        const hasScreenshot = step.data?.screenshot;
                                                        const isSelected = selectedStepIndex === index;
                                                        // Check if selected index is in ANY of the duplicate indices for this step
                                                        const isHighlighted = stepsToRender.duplicateRanges[filteredIndex].includes(selectedStepIndex) ||
                                                                            (stepsToRender.duplicateRanges[filteredIndex].includes(displayedScreenshot?.stepIndex) && selectedStepIndex === null);
                                                        
                                                        // Check if this is an evaluation step
                                                        const isEvaluationStep = step.type === "evaluation_result";
                                                        
                                                        // Use overall run result for evaluation steps (consistent with footer)
                                                        const overallSuccess = completionData?.success ?? runData?.success;
                                                        const shouldUseSuccessColor = isEvaluationStep && overallSuccess !== undefined;
                                                        
                                                        return (
                                                            <button
                                                                key={index}
                                                                onClick={() => handleStepClick(index)}
                                                                className={cn(
                                                                    "relative w-full text-left pl-6 pr-2 py-1.5 transition-all group cursor-pointer hover:bg-cyan-500/5"
                                                                )}
                                                            >
                                                                {/* Timeline dot - emerald for success, red for failure on evaluation steps */}
                                                                <div className={cn(
                                                                    "absolute left-[5px] top-2 h-2 w-2 rounded-full border-2 transition-all z-10",
                                                                    shouldUseSuccessColor
                                                                        ? overallSuccess
                                                                            ? "bg-emerald-400 border-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
                                                                            : "bg-red-400 border-red-400 shadow-[0_0_8px_rgba(239,68,68,0.6)]"
                                                                        : isSelected || isHighlighted
                                                                            ? "bg-cyan-400 border-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.6)]"
                                                                            : hasScreenshot 
                                                                                ? "bg-purple-500/50 border-purple-400/50 group-hover:bg-cyan-400/50 group-hover:border-cyan-400/50" 
                                                                                : "bg-purple-900 border-purple-700 group-hover:bg-purple-700 group-hover:border-purple-700"
                                                                )} />
                                                                
                                                                <div className="flex items-start justify-between gap-2 min-w-0">
                                                                    <span className={cn(
                                                                        "text-xs font-mono break-words flex-1",
                                                                        shouldUseSuccessColor
                                                                            ? overallSuccess 
                                                                                ? "text-emerald-400" 
                                                                                : "text-red-400"
                                                                            : isSelected || isHighlighted 
                                                                                ? "text-cyan-300" 
                                                                                : "text-purple-300/90"
                                                                    )}>
                                                                        {step.name}
                                                                    </span>
                                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                                        {step.timestamp && (
                                                                            <span className="text-[10px] font-mono text-purple-400/50">
                                                                                {new Date(step.timestamp).toLocaleTimeString('en-US', { 
                                                                                    hour12: false,
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                    second: '2-digit'
                                                                                })}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                        );
                                                    });
                                                })()}
                                                
                                                {!isComplete && isConnected && (
                                                    <div className="relative w-full text-left pl-6 pr-2 py-1.5" aria-label="Live">
                                                        {/* Solid timeline dot */}
                                                        <div className="absolute left-[5px] top-2 h-2 w-2 rounded-full bg-purple-500/10" />
                                                        {/* Skeleton row */}
                                                        <div className="flex items-start justify-between gap-2 min-w-0">
                                                            {/* Pulsing text skeleton */}
                                                            <div className="h-3 flex-1 max-w-[60%] rounded bg-purple-500/10 animate-pulse" />
                                                            {/* Timestamp skeleton */}
                                                            <div className="h-3 w-12 rounded bg-purple-500/10 animate-pulse shrink-0" />
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Invisible element to scroll to */}
                                                <div ref={timelineEndRef} />
                                            </>
                                        )}
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>

                        {/* Right Panel (1/3 width) - Run Data & Details */}
                        <div className="flex flex-col h-full min-h-[300px]">
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-sm font-semibold text-cyan-400">Run Data</h3>
                                <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/50 to-transparent" />
                            </div>
                            
                            <ScrollArea className="flex-1 bg-black/60 backdrop-blur-sm border border-cyan-500/30 rounded-lg p-4">
                                <div className="space-y-3">
                                    {!runData ? (
                                        <div className="space-y-4">
                                            {[...Array(4)].map((_, i) => (
                                                <div key={i} className="space-y-2">
                                                    <div className="h-4 w-24 bg-cyan-500/10 rounded animate-pulse" />
                                                    <div className="h-3 bg-cyan-500/5 rounded animate-pulse" style={{ width: `${70 + Math.random() * 20}%` }} />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <>
                                            {/* ERROR_MESSAGE - Only show when there's an error */}
                                            {isComplete && runData?.success === false && runData?.note && (
                                            <DataSection 
                                                title="Error Message" 
                                                icon={XCircle} 
                                                defaultExpanded={true}
                                                disabled={false}
                                            >
                                                    <div className="bg-red-500/10 rounded border border-red-500/30 p-2">
                                                        <p className="text-xs font-mono text-red-400">
                                                            {runData.note}
                                                        </p>
                                                    </div>
                                                </DataSection>
                                            )}

                                            {/* METRICS - Always show for all action types */}
                                            <DataSection 
                                                title="Metrics" 
                                                icon={Activity} 
                                                defaultExpanded={true}
                                                alwaysExpanded={true}
                                                disabled={!runData}
                                            >
                                                {runData && (
                                                    <div className="space-y-2 text-xs font-mono">
                                                        {runData.actionType && (
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-purple-300/70">ACTION_TYPE:</span>
                                                                <span className="text-cyan-400">{runData.actionType.toUpperCase()}</span>
                                                            </div>
                                                        )}
                                                        {!isComplete && runData.startTime && (
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-purple-300/70">DURATION:</span>
                                                                <span className="text-cyan-400">{formatDuration(liveDuration)}</span>
                                                            </div>
                                                        )}
                                                        {isComplete && runData?.duration && (
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-purple-300/70">DURATION:</span>
                                                                <span className="text-cyan-400">{formatDuration(runData.duration)}</span>
                                                            </div>
                                                        )}
                                                        {isComplete && (completionData?.result?.responseTime || runData?.result?.responseTime) && (
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-purple-300/70">
                                                                    {runData?.actionType === 'browser_agent' ? 'AVG_RESPONSE_TIME:' : 'RESPONSE_TIME:'}
                                                                </span>
                                                                <span className="text-cyan-400">
                                                                    {/* Use pre-formatted display string if available, otherwise format client-side */}
                                                                    {completionData?.result?.responseTimeDisplay || 
                                                                     runData?.result?.responseTimeDisplay ||
                                                                     formatDuration(completionData?.result?.responseTime || runData?.result?.responseTime)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </DataSection>

                                            {/* BOT_INSIGHTS - Always show for all action types */}
                                            <DataSection 
                                                title="Bot Insights" 
                                                icon={StatusBotIcon} 
                                                iconClassName="h-4 w-4 text-cyan-400" 
                                                defaultExpanded={isComplete}
                                                disabled={!runData?.note || runData?.success === false}
                                            >
                                                {runData?.note && (
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className={cn(
                                                            "text-xs font-mono flex-1",
                                                            runData?.success !== false ? "text-emerald-400" : "text-green-400"
                                                        )}>
                                                            {runData.note}
                                                        </p>
                                                        <CopyButton text={runData.note} />
                                                    </div>
                                                )}
                                            </DataSection>

                                            {/* REQUEST_URL - Show for ping and browser_agent */}
                                            {(runData?.actionType === 'ping' || runData?.actionType === 'browser_agent') && (
                                                <DataSection 
                                                    title="Request URL" 
                                                    icon={Link} 
                                                    defaultExpanded={runData?.actionType === 'ping' && !!completionData?.result?.url}
                                                    disabled={!completionData?.result?.url}
                                                >
                                                    {completionData?.result?.url && (
                                                        <div className="flex items-center justify-between gap-2">
                                                            <TruncatedUrl 
                                                                url={completionData.result.url}
                                                                maxLength={40}
                                                                    className="text-xs font-mono text-purple-300/70"
                                                            />
                                                            <CopyButton text={completionData.result.url} />
                                                        </div>
                                                    )}
                                                </DataSection>
                                            )}

                                            {/* AGENT_INSTRUCTIONS - Show for browser_agent only */}
                                            {runData?.actionType === 'browser_agent' && (
                                                <DataSection 
                                                    title="Agent Instructions" 
                                                    icon={Code2} 
                                                    defaultExpanded={false}
                                                    disabled={!completionData?.result?.prompt}
                                                >
                                                    {completionData?.result?.prompt && (
                                                        <div className="flex items-start justify-between gap-2">
                                                            <p className="text-xs font-mono text-purple-300/70 flex-1">
                                                                {completionData.result.prompt}
                                                            </p>
                                                            <CopyButton text={completionData.result.prompt} />
                                                        </div>
                                                    )}
                                                </DataSection>
                                            )}

                                            {/* SSL_VALIDATION - Show for ping action only */}
                                            {runData?.actionType === 'ping' && (
                                                <DataSection 
                                                    title={`SSL Validation ${completionData?.result?.sslError ? '[Failed]' : completionData?.result?.sslValidated ? '[Passed]' : ''}`}
                                                    icon={Shield} 
                                                    defaultExpanded={false}
                                                    disabled={!completionData?.result?.sslValidated}
                                                >
                                                    {completionData?.result?.sslValidated && (
                                                        <>
                                                            {completionData.result.sslInfo ? (
                                                                <div className="space-y-2 text-xs font-mono">
                                                                    {completionData.result.sslInfo.subject && (
                                                                        <div className="flex justify-between items-start gap-2">
                                                                            <span className="text-purple-300/70">SUBJECT:</span>
                                                                            <span className="text-cyan-400 text-right">
                                                                                {completionData.result.sslInfo.subject.commonName || 
                                                                                 completionData.result.sslInfo.subject.CN || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {completionData.result.sslInfo.issuer && (
                                                                        <div className="flex justify-between items-start gap-2">
                                                                            <span className="text-purple-300/70">ISSUER:</span>
                                                                            <span className="text-cyan-400 text-right">
                                                                                {completionData.result.sslInfo.issuer.organizationName || 
                                                                                 completionData.result.sslInfo.issuer.O || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {completionData.result.sslInfo.notBefore && (
                                                                        <div className="flex justify-between items-start gap-2">
                                                                            <span className="text-purple-300/70">VALID_FROM:</span>
                                                                            <span className="text-cyan-400 text-right">
                                                                                {completionData.result.sslInfo.notBefore}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {completionData.result.sslInfo.notAfter && (
                                                                        <div className="flex justify-between items-start gap-2">
                                                                            <span className="text-purple-300/70">VALID_UNTIL:</span>
                                                                            <span className="text-cyan-400 text-right">
                                                                                {completionData.result.sslInfo.notAfter}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <p className="text-xs font-mono text-purple-300/70">
                                                                    {completionData.result.sslError 
                                                                        ? "Certificate validation failed"
                                                                        : "Certificate validation passed"}
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                                </DataSection>
                                            )}

                                            {/* RESPONSE_HEADERS - Show for ping only */}
                                            {runData?.actionType === 'ping' && (
                                                <DataSection 
                                                    title={`Response Headers ${completionData?.result?.headers ? `[${Object.keys(completionData.result.headers).length}]` : ''}`}
                                                    icon={FileJson} 
                                                    defaultExpanded={false}
                                                    disabled={!completionData?.result?.headers || Object.keys(completionData.result.headers).length === 0}
                                                >
                                                    {completionData?.result?.headers && Object.keys(completionData.result.headers).length > 0 && (
                                                        <div className="space-y-1.5 text-xs font-mono max-h-48 overflow-y-auto">
                                                            {Object.entries(completionData.result.headers).map(([key, value]) => (
                                                                <div key={key} className="flex justify-between items-start gap-2 border-b border-purple-800/50 pb-1">
                                                                    <span className="text-purple-300/70 shrink-0">{key}:</span>
                                                                    <span className="text-cyan-400 text-right break-all">{value}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </DataSection>
                                            )}

                                            {/* RESPONSE_DATA - Show for ping only */}
                                            {runData?.actionType === 'ping' && (
                                                <DataSection 
                                                    title="Response Data" 
                                                    icon={FileJson} 
                                                    defaultExpanded={false}
                                                    disabled={!completionData?.result?.data}
                                                >
                                                    {completionData?.result?.data && (
                                                        <div className="relative group">
                                                            <div className="absolute right-0 top-0">
                                                                <CopyButton 
                                                                    text={typeof completionData.result.data === 'string'
                                                                        ? completionData.result.data
                                                                        : JSON.stringify(completionData.result.data, null, 2)}
                                                                />
                                                            </div>
                                                            <pre className="text-xs font-mono text-purple-300/70 overflow-x-auto max-h-64 bg-black/40 rounded p-2 border border-purple-800/50">
                                                                {typeof completionData.result.data === 'string'
                                                                    ? completionData.result.data
                                                                    : JSON.stringify(completionData.result.data, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </DataSection>
                                            )}

                                            {/* BROWSER_INFO - Show for browser_agent only */}
                                            {runData?.actionType === 'browser_agent' && (
                                                <DataSection 
                                                    title="Browser Info" 
                                                    icon={Monitor} 
                                                    defaultExpanded={false}
                                                    disabled={!completionData?.result?.browserInfo}
                                                >
                                                    {completionData?.result?.browserInfo && (
                                                        <div className="space-y-2 text-xs font-mono">
                                                            <div className="flex justify-between items-center">
                                                                <span className="text-purple-300/70">TYPE:</span>
                                                                <span className="text-cyan-400">
                                                                    {completionData.result.browserInfo.useLocalBrowser ? "LOCAL" : "STEEL"}
                                                                </span>
                                                            </div>
                                                            {completionData.result.browserInfo.steelSessionId && (
                                                                <div className="flex justify-between items-start gap-2">
                                                                    <span className="text-purple-300/70">SESSION_ID:</span>
                                                                    <span className="text-cyan-400 text-right break-all">
                                                                        {completionData.result.browserInfo.steelSessionId}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {completionData.result.browserInfo.tracePath && (
                                                                <div className="flex justify-between items-start gap-2">
                                                                    <span className="text-purple-300/70">TRACE_PATH:</span>
                                                                    <span className="text-cyan-400 text-right break-all">
                                                                        {completionData.result.browserInfo.tracePath}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Network Information */}
                                                            {completionData.result.browserInfo.networkInfo && (
                                                                <>
                                                                    <div className="h-px bg-cyan-500/30 my-2" />
                                                                    {/* Average Page Load Time */}
                                                                    {completionData.result?.responseTime && (
                                                                        <div className="flex justify-between items-center">
                                                                            <span className="text-purple-300/70">AVG_PAGE_LOAD:</span>
                                                                            <span className="text-cyan-400">
                                                                                {formatDuration(completionData.result.responseTime)}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-purple-300/70">NETWORK:</span>
                                                                        <span className="text-cyan-400">
                                                                            {completionData.result.browserInfo.networkInfo.effectiveType?.toUpperCase()}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex justify-between items-center">
                                                                        <span className="text-purple-300/70">BANDWIDTH:</span>
                                                                        <span className="text-cyan-400">
                                                                            {completionData.result.browserInfo.networkInfo.downlink} Mbps
                                                                        </span>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )}
                                                </DataSection>
                                            )}
                                        </>
                                    )}
                                </div>
                            </ScrollArea>
                        </div>
                    </div>
                </div>

                {/* Completion Banner */}
                {isComplete && (
                    <div className={cn(
                        "relative border-t px-6 py-3 backdrop-blur-sm",
                        completionData?.success 
                            ? "border-emerald-500/50 bg-emerald-500/10" 
                            : "border-red-500/50 bg-red-500/10"
                    )}>
                        <div className="flex items-center gap-3">
                            {completionData?.success ? (
                                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                            ) : (
                                <XCircle className="h-5 w-5 text-red-400" />
                            )}
                            <div>
                                <p className={cn(
                                    "text-sm font-semibold",
                                    completionData?.success ? "text-emerald-400" : "text-red-400"
                                )}>
                                    {completionData?.success ? 'Check passed' : 'Check failed'}
                                </p>
                                {runData?.duration && (
                                    <p className="text-xs text-purple-300/70">
                                        Duration: {formatDuration(runData.duration)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>

            {/* Screenshot Lightbox */}
            {lightboxScreenshot && (
                <Dialog open={!!lightboxScreenshot} onOpenChange={() => setLightboxScreenshot(null)}>
                    <DialogContent className="max-w-7xl p-0 overflow-hidden bg-black border-cyan-500/50">
                        <div className="relative">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-2 top-2 z-10 bg-black/80 hover:bg-black/90 text-cyan-400 border border-cyan-500/50"
                                onClick={() => setLightboxScreenshot(null)}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                            <img 
                                src={lightboxScreenshot?.startsWith('data:') 
                                    ? lightboxScreenshot 
                                    : `data:image/png;base64,${lightboxScreenshot}`}
                                alt="Screenshot full view"
                                className="w-full h-auto max-h-[95vh] object-contain"
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </Dialog>
    );
}

